import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Mail, Plus, Trash2, Check, X, Loader2, Clock, Sun, Moon } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function EmailSettings({ user, locations }) {
  const [emailSettings, setEmailSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLocation, setSelectedLocation] = useState("");
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newRecipient, setNewRecipient] = useState({
    email: "",
    morning_reminder: true,
    afternoon_reminder: true,
    daily_report: true
  });
  const [saving, setSaving] = useState(false);
  // Delete-confirm dialog — replaces window.confirm which is silently blocked
  // in some browser/webview contexts (previous bug: trash icon appeared inert
  // in production because window.confirm never surfaced).
  const [pendingDelete, setPendingDelete] = useState(null); // { locationId, email }
  const [deleting, setDeleting] = useState(false);

  const isSuperAdmin = user?.role === "super_admin";
  const isAdmin = ["admin", "tenant_admin"].includes(user?.role) || isSuperAdmin;

  useEffect(() => {
    if (isAdmin) {
      fetchEmailSettings();
    }
  }, [isAdmin]);

  useEffect(() => {
    // Auto-select location for location admins
    if (!isSuperAdmin && user?.location_id && !selectedLocation) {
      setSelectedLocation(localStorage.getItem('currentLocationId') || user.location_id);
    }
  }, [user, isSuperAdmin, selectedLocation]);

  const fetchEmailSettings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/email-settings`);
      setEmailSettings(response.data);
    } catch (error) {
      console.error("Failed to fetch email settings:", error);
      toast.error("Failed to load email settings");
    } finally {
      setLoading(false);
    }
  };

  const addRecipient = async () => {
    if (!selectedLocation) {
      toast.error("Please select a location first");
      return;
    }
    if (!newRecipient.email) {
      toast.error("Please enter an email address");
      return;
    }
    if (!newRecipient.email.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/admin/email-settings/${selectedLocation}/recipient`, newRecipient);
      toast.success(`Added ${newRecipient.email} to email notifications`);
      setAddDialogOpen(false);
      setNewRecipient({
        email: "",
        morning_reminder: true,
        afternoon_reminder: true,
        daily_report: true
      });
      fetchEmailSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add recipient");
    } finally {
      setSaving(false);
    }
  };

  const updateRecipient = async (locationId, email, field, value) => {
    try {
      await axios.put(
        `${API}/admin/email-settings/${locationId}/recipient/${encodeURIComponent(email)}`,
        { [field]: value }
      );
      
      // Update local state
      setEmailSettings(prev => prev.map(loc => {
        if (loc.location_id === locationId) {
          return {
            ...loc,
            recipients: loc.recipients.map(r => 
              r.email === email ? { ...r, [field]: value } : r
            )
          };
        }
        return loc;
      }));
      
      toast.success("Settings updated");
    } catch (error) {
      toast.error("Failed to update settings");
    }
  };

  const deleteRecipient = async (locationId, email) => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/email-settings/${locationId}/recipient/${encodeURIComponent(email)}`);
      toast.success(`Removed ${email} from notifications`);
      setPendingDelete(null);
      fetchEmailSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove recipient");
    } finally {
      setDeleting(false);
    }
  };

  const currentLocationSettings = emailSettings.find(s => s.location_id === selectedLocation);

  if (!isAdmin) {
    return null;
  }

  return (
    <Card data-testid="email-settings-card">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Mail className="h-5 w-5 text-blue-500" />
          <CardTitle>Email Notification Settings</CardTitle>
        </div>
        <CardDescription>
          Configure who receives daily emails for each location
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Email Schedule Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-medium text-blue-800 mb-2">Email Schedule (EST)</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="flex items-center gap-2">
              <Sun className="h-4 w-4 text-amber-500" />
              <span><strong>8:00 AM</strong> - Morning Action List</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-blue-500" />
              <span><strong>2:00 PM</strong> - Afternoon Check-In</span>
            </div>
            <div className="flex items-center gap-2">
              <Moon className="h-4 w-4 text-indigo-500" />
              <span><strong>7:15 PM</strong> - Daily Activity Report</span>
            </div>
          </div>
        </div>

        {/* Location Selector */}
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <label className="text-sm font-medium mb-1 block">Select Location</label>
            <Select 
              value={selectedLocation} 
              onValueChange={setSelectedLocation}
              disabled={!isSuperAdmin}
            >
              <SelectTrigger data-testid="location-selector">
                <SelectValue placeholder="Select a location..." />
              </SelectTrigger>
              <SelectContent>
                {locations.map(loc => (
                  <SelectItem key={loc.id} value={loc.id}>
                    {loc.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="mt-6" 
                disabled={!selectedLocation}
                data-testid="add-recipient-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Recipient
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add Email Recipient</DialogTitle>
                <DialogDescription>
                  Add a new email address to receive notifications for this location
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <label className="text-sm font-medium">Email Address</label>
                  <Input
                    type="email"
                    placeholder="email@example.com"
                    value={newRecipient.email}
                    onChange={(e) => setNewRecipient({ ...newRecipient, email: e.target.value })}
                    data-testid="new-recipient-email"
                  />
                </div>
                <div className="space-y-3">
                  <label className="text-sm font-medium">Email Types</label>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="morning"
                        checked={newRecipient.morning_reminder}
                        onCheckedChange={(checked) => setNewRecipient({ ...newRecipient, morning_reminder: checked })}
                      />
                      <label htmlFor="morning" className="text-sm">
                        <Sun className="h-3 w-3 inline mr-1 text-amber-500" />
                        Morning Reminder (8 AM)
                      </label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="afternoon"
                        checked={newRecipient.afternoon_reminder}
                        onCheckedChange={(checked) => setNewRecipient({ ...newRecipient, afternoon_reminder: checked })}
                      />
                      <label htmlFor="afternoon" className="text-sm">
                        <Clock className="h-3 w-3 inline mr-1 text-blue-500" />
                        Afternoon Check-In (2 PM)
                      </label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="daily"
                        checked={newRecipient.daily_report}
                        onCheckedChange={(checked) => setNewRecipient({ ...newRecipient, daily_report: checked })}
                      />
                      <label htmlFor="daily" className="text-sm">
                        <Moon className="h-3 w-3 inline mr-1 text-indigo-500" />
                        Daily Activity Report (7:15 PM)
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setAddDialogOpen(false)}>Cancel</Button>
                <Button onClick={addRecipient} disabled={saving} data-testid="save-recipient-btn">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Add Recipient
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Recipients Table */}
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : selectedLocation ? (
          currentLocationSettings?.recipients?.length > 0 ? (
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email Address</TableHead>
                    <TableHead className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Sun className="h-3 w-3 text-amber-500" />
                        Morning
                      </div>
                    </TableHead>
                    <TableHead className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Clock className="h-3 w-3 text-blue-500" />
                        Afternoon
                      </div>
                    </TableHead>
                    <TableHead className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Moon className="h-3 w-3 text-indigo-500" />
                        Daily Report
                      </div>
                    </TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {currentLocationSettings.recipients.map((recipient) => (
                    <TableRow key={recipient.email} data-testid={`recipient-row-${recipient.email}`}>
                      <TableCell className="font-medium">{recipient.email}</TableCell>
                      <TableCell className="text-center">
                        <Checkbox
                          checked={recipient.morning_reminder}
                          onCheckedChange={(checked) => 
                            updateRecipient(selectedLocation, recipient.email, 'morning_reminder', checked)
                          }
                        />
                      </TableCell>
                      <TableCell className="text-center">
                        <Checkbox
                          checked={recipient.afternoon_reminder}
                          onCheckedChange={(checked) => 
                            updateRecipient(selectedLocation, recipient.email, 'afternoon_reminder', checked)
                          }
                        />
                      </TableCell>
                      <TableCell className="text-center">
                        <Checkbox
                          checked={recipient.daily_report}
                          onCheckedChange={(checked) => 
                            updateRecipient(selectedLocation, recipient.email, 'daily_report', checked)
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={() => setPendingDelete({ locationId: selectedLocation, email: recipient.email })}
                          data-testid={`delete-${recipient.email}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500 border rounded-lg bg-gray-50">
              <Mail className="h-8 w-8 mx-auto mb-2 text-gray-400" />
              <p>No email recipients configured for this location</p>
              <p className="text-sm mt-1">Click "Add Recipient" to get started</p>
            </div>
          )
        ) : (
          <div className="text-center py-8 text-gray-500 border rounded-lg bg-gray-50">
            <p>Select a location to manage email recipients</p>
          </div>
        )}

        {/* Summary for Super Admins */}
        {isSuperAdmin && emailSettings.length > 0 && (
          <div className="mt-6">
            <h4 className="font-medium text-sm text-gray-700 mb-3">All Locations Summary</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {emailSettings.map(loc => (
                <div 
                  key={loc.location_id} 
                  className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                    selectedLocation === loc.location_id ? 'border-blue-500 bg-blue-50' : 'hover:bg-gray-50'
                  }`}
                  onClick={() => setSelectedLocation(loc.location_id)}
                >
                  <div className="font-medium text-sm">{loc.location_name}</div>
                  <div className="text-xs text-gray-500 mt-1">
                    {loc.recipients?.length || 0} recipient(s)
                  </div>
                  {loc.recipients?.length > 0 && (
                    <div className="flex gap-1 mt-2">
                      {loc.recipients.some(r => r.morning_reminder) && (
                        <Badge variant="outline" className="text-xs py-0">
                          <Sun className="h-2 w-2 mr-1" />AM
                        </Badge>
                      )}
                      {loc.recipients.some(r => r.afternoon_reminder) && (
                        <Badge variant="outline" className="text-xs py-0">
                          <Clock className="h-2 w-2 mr-1" />PM
                        </Badge>
                      )}
                      {loc.recipients.some(r => r.daily_report) && (
                        <Badge variant="outline" className="text-xs py-0">
                          <Moon className="h-2 w-2 mr-1" />Daily
                        </Badge>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>

      {/* Delete-confirm dialog: shadcn AlertDialog renders inside the app
          shell so it always shows, unlike native window.confirm which is
          silently suppressed in some webview / production contexts. */}
      <AlertDialog
        open={!!pendingDelete}
        onOpenChange={(open) => { if (!open) setPendingDelete(null); }}
      >
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove from email notifications?</AlertDialogTitle>
            <AlertDialogDescription>
              <b>{pendingDelete?.email}</b> will stop receiving morning, afternoon and daily report emails for this location. This cannot be undone from this screen (you can re-add them anytime).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} data-testid="delete-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-500"
              onClick={(e) => {
                // Prevent AlertDialog auto-close so we can await the request
                // and only close on success (finally does setPendingDelete(null)).
                e.preventDefault();
                if (pendingDelete) {
                  deleteRecipient(pendingDelete.locationId, pendingDelete.email);
                }
              }}
              data-testid="delete-confirm-btn"
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
